/**
 * Map.
 *
 * §24 preserves: numbered pins, current-stop styling, completed-stop styling,
 * team differentiation, road geometry, team filter, stop list, Navigate, the
 * mobile bottom sheet, and the desktop operational pane. Map pins remain
 * numbered; authorized names appear only in the adjacent operational list.
 *
 * §24 / §62 / §65: Leaflet is bundled — the import below resolves to the local
 * package and the CSS is imported into the build. Tiles come from the in-boundary
 * tile service, never a public OpenStreetMap server. OSM attribution is preserved
 * because the underlying data is still theirs.
 */

import { useEffect, useRef, useState, type DragEvent } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css'; // §62: bundled, never from unpkg
import {
  Bars3Icon, ChevronDownIcon, ChevronUpIcon,
} from '@heroicons/react/24/outline';

export interface MapStop {
  id: string;
  routePlanId: string;
  routeStarted: boolean;
  sequence: number;
  lat: number;
  lng: number;
  teamId: string;
  teamName: string;
  status: 'pending' | 'current' | 'completed' | 'skipped';
  addressLine: string;
  cityLine: string;
  recordCount: number;
  personNames: string[];
  navigateUrl: string | null;
}

export interface StarlinkLocation {
  id: string;
  /** Full device identifier is returned only while the terminal is unassigned. */
  terminalId: string | null;
  teamId: string | null;
  teamName: string | null;
  h3CellId: string;
  center: [number, number];
  boundary: Array<[number, number]>;
  reportedAt: string | null;
  status: 'online' | 'stale' | 'offline' | 'unassigned';
  signalPercent: number | null;
  latencyMs: number | null;
  packetLossPercent: number | null;
  downlinkMbps: number | null;
  uplinkMbps: number | null;
  obstructionPercent: number | null;
  uptimeSeconds: number | null;
  softwareVersion: string | null;
  countryCode: string | null;
  inTerritorialWaters: boolean | null;
  secondsUntilSoftwareUpdateRebootPossible: number | null;
  alertCount: number;
  activeAlerts: string[];
  /** Starlink's supported API does not provide motion data. */
  velocityKph: null;
  directionOfTravel: null;
}

export interface MapData {
  stops: MapStop[];
  dateLabel: string;
  teams: Array<{ id: string; name: string; color: string }>;
  /** Encoded polylines per team, from the private routing service. §24 §64 */
  geometry: Array<{ teamId: string; points: Array<[number, number]> }>;
  /** Approximate Starlink H3 areas. These are terminal areas, not person GPS. */
  starlinkLocations: StarlinkLocation[];
  /** In-boundary tile endpoint. Never a public tile server. §65 */
  tileUrl: string;
  attribution: string;
}

export function MapScreen({
  data, selectedTeamId, canReorder = false, onTeamFilter, onSelectStop,
  onOpenRoute, onReorder,
}: {
  data: MapData | null;
  selectedTeamId: string | 'all';
  canReorder?: boolean;
  onTeamFilter: (id: string | 'all') => void;
  onSelectStop: (stopId: string) => void;
  onOpenRoute: (teamId: string) => void;
  onReorder?: (routePlanId: string, stopIds: string[]) => Promise<MapData>;
}) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const [selected, setSelected] = useState<MapStop | null>(null);
  const [selectedStarlinkId, setSelectedStarlinkId] = useState<string | null>(null);
  const [orderedStops, setOrderedStops] = useState<MapStop[]>([]);
  const [draggedStopId, setDraggedStopId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveMessage, setSaveMessage] = useState('');
  const activeTeam = data?.teams.find((t) => t.id === selectedTeamId);
  const visibleStops = orderedStops.filter(
    (s) => selectedTeamId === 'all' || s.teamId === selectedTeamId,
  );
  const visibleStarlink = data?.starlinkLocations.filter(
    (location) => selectedTeamId === 'all' || location.teamId === selectedTeamId,
  ) ?? [];
  const selectedStarlink = visibleStarlink.find(
    (location) => location.id === selectedStarlinkId,
  ) ?? null;

  useEffect(() => {
    setOrderedStops(data?.stops ?? []);
    setSelected((current) => current
      ? data?.stops.find((stop) => stop.id === current.id) ?? null
      : null);
  }, [data]);

  useEffect(() => {
    setDraggedStopId(null);
    setDropTargetId(null);
    setSaveState('idle');
    setSaveMessage('');
  }, [selectedTeamId]);

  const routePlanId = selectedTeamId === 'all' ? null : visibleStops[0]?.routePlanId ?? null;
  const routeStarted = visibleStops.some((stop) => stop.routeStarted);
  const reorderEnabled = canReorder && Boolean(onReorder) && Boolean(routePlanId)
    && !routeStarted && saveState !== 'saving' && visibleStops.length > 1;

  const saveOrder = async (nextVisible: MapStop[]) => {
    if (!reorderEnabled || !onReorder || !routePlanId) return;
    const previous = orderedStops;
    const renumbered = nextVisible.map((stop, index) => ({ ...stop, sequence: index + 1 }));
    const replacements = new Map(renumbered.map((stop) => [stop.id, stop]));
    const nextAll = orderedStops.map((stop) => replacements.get(stop.id) ?? stop);
    setOrderedStops(nextAll);
    setSelected((current) => current ? replacements.get(current.id) ?? current : null);
    setSaveState('saving');
    setSaveMessage('Saving new route order...');
    try {
      const saved = await onReorder(routePlanId, renumbered.map((stop) => stop.id));
      setOrderedStops(saved.stops);
      setSelected((current) => current
        ? saved.stops.find((stop) => stop.id === current.id) ?? null
        : null);
      setSaveState('saved');
      setSaveMessage('Route order saved.');
    } catch {
      setOrderedStops(previous);
      setSelected((current) => current
        ? previous.find((stop) => stop.id === current.id) ?? null
        : null);
      setSaveState('error');
      setSaveMessage('The order was not saved. Refresh the map and try again.');
    }
  };

  const moveStop = (sourceId: string, targetIndex: number) => {
    const sourceIndex = visibleStops.findIndex((stop) => stop.id === sourceId);
    if (sourceIndex < 0 || sourceIndex === targetIndex) return;
    const next = [...visibleStops];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    void saveOrder(next);
  };

  const beginDrag = (event: DragEvent<HTMLButtonElement>, stopId: string) => {
    if (!reorderEnabled) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', stopId);
    setDraggedStopId(stopId);
  };

  const dropStop = (event: DragEvent<HTMLLIElement>, targetIndex: number) => {
    event.preventDefault();
    const sourceId = event.dataTransfer.getData('text/plain') || draggedStopId;
    setDraggedStopId(null);
    setDropTargetId(null);
    if (sourceId) moveStop(sourceId, targetIndex);
  };

  useEffect(() => {
    if (!container.current || map.current || !data) return;

    map.current = L.map(container.current, {
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer(data.tileUrl, {
      maxZoom: 18,
      // §65: attribution is preserved even though the tiles are self-hosted.
      attribution: data.attribution,
    }).addTo(map.current);

    layer.current = L.layerGroup().addTo(map.current);

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, [data]);

  useEffect(() => {
    if (!map.current || !layer.current || !data) return;
    layer.current.clearLayers();

    const visible = selectedTeamId === 'all'
      ? orderedStops
      : orderedStops.filter((s) => s.teamId === selectedTeamId);

    for (const g of data.geometry) {
      if (selectedTeamId !== 'all' && g.teamId !== selectedTeamId) continue;
      const team = data.teams.find((t) => t.id === g.teamId);
      L.polyline(g.points, {
        color: team?.color ?? '#0088b0',
        weight: 3,
        opacity: 0.7,
      }).addTo(layer.current);
    }

    for (const s of visible) {
      const team = data.teams.find((t) => t.id === s.teamId);
      L.marker([s.lat, s.lng], {
        icon: pinIcon(s, team?.color ?? '#0088b0'),
        // §24: the accessible name is the SEQUENCE, never a person's name.
        alt: `Stop ${s.sequence}`,
        keyboard: true,
      })
        .on('click', () => { setSelected(s); onSelectStop(s.id); })
        .addTo(layer.current!);
    }

    const visibleLocations = selectedTeamId === 'all'
      ? data.starlinkLocations
      : data.starlinkLocations.filter((location) => location.teamId === selectedTeamId);
    for (const location of visibleLocations) {
      const team = data.teams.find((candidate) => candidate.id === location.teamId);
      const color = team?.color ?? '#686463';
      const label = location.teamName
        ? `${location.teamName} Starlink area, ${location.status}`
        : `Unassigned Starlink ${location.terminalId ?? 'terminal'}`;
      const tooltip = document.createElement('span');
      tooltip.textContent = label;
      const selectTerminal = () => {
        setSelectedStarlinkId(location.id);
        setSelected(null);
      };
      L.polygon(location.boundary, {
        color,
        weight: 2,
        opacity: 0.9,
        fillColor: color,
        fillOpacity: location.status === 'online' ? 0.16 : 0.07,
        dashArray: location.status === 'online' ? undefined : '5 5',
      }).bindTooltip(tooltip).on('click', selectTerminal).addTo(layer.current);
      const markerTooltip = document.createElement('span');
      markerTooltip.textContent = label;
      L.marker(location.center, {
        icon: starlinkIcon(location.teamId === null),
        alt: label,
        keyboard: true,
        riseOnHover: true,
      }).bindTooltip(markerTooltip).on('click', selectTerminal).addTo(layer.current);
    }

    const bounds = [
      ...visible.map((s) => [s.lat, s.lng] as [number, number]),
      ...visibleLocations.map((location) => location.center),
    ];
    if (bounds.length > 0) {
      map.current.fitBounds(
        L.latLngBounds(bounds),
        { padding: [40, 40] },
      );
    }
  }, [data, orderedStops, selectedTeamId, onSelectStop]);

  return (
    <div className="flex flex-col desktop:grid desktop:grid-cols-[285px_minmax(0,1fr)] desktop:h-[calc(100vh-64px)] min-h-0 overflow-hidden">
      <div className="order-1 desktop:order-2 h-[42vh] desktop:h-full min-h-[280px]">
        <div
          ref={container}
          role="application"
          aria-label="Route map"
          className="w-full h-full bg-surface"
        />
      </div>

      <aside
        className="order-2 desktop:order-1 bg-bg px-4 py-3 desktop:py-4
                   flex flex-col min-h-0 max-h-[48vh] desktop:max-h-none desktop:overflow-hidden"
        aria-label="Stops"
      >
        <div className="desktop:hidden w-9 h-1 rounded-full bg-neutral-400 mx-auto mb-2" />
        <div className="flex items-baseline justify-between gap-2">
          <h4 className="truncate text-[18px]">
            {data?.dateLabel ?? 'Route'} · {activeTeam?.name ?? 'All teams'}
          </h4>
          <button className="btn btn-ghost px-0 shrink-0"
            disabled={selectedTeamId === 'all'}
            onClick={() => selectedTeamId !== 'all' && onOpenRoute(selectedTeamId)}>
            Open route ›
          </button>
        </div>

        <div className="flex gap-2 overflow-x-auto py-2 shrink-0 scrollbar-none">
          <button
            onClick={() => onTeamFilter('all')}
            aria-pressed={selectedTeamId === 'all'}
            className={`seg ${selectedTeamId === 'all' ? 'seg-active' : ''}`}
          >
            All teams
          </button>
          {data?.teams.map((t) => (
            <button
              key={t.id}
              onClick={() => onTeamFilter(t.id)}
              aria-pressed={selectedTeamId === t.id}
              className={`seg ${selectedTeamId === t.id ? 'seg-active' : ''}`}
            >
              {t.name}
            </button>
          ))}
        </div>

        {canReorder && visibleStops.length > 1 && (
          <div className="shrink-0 pb-2">
            <p className="text-sm text-neutral-700">
              {selectedTeamId === 'all'
                ? 'Choose one team to change its stop order.'
                : routeStarted
                  ? 'Route order is locked because field work has started.'
                  : 'Drag a handle, or use the arrow buttons, to reorder this route.'}
            </p>
            {saveMessage && (
              <p
                role="status"
                aria-live="polite"
                className={`mt-1 text-sm ${saveState === 'error' ? 'text-alert-700' : 'text-neutral-700'}`}
              >
                {saveMessage}
              </p>
            )}
          </div>
        )}

        {visibleStarlink.length > 0 && (
          <section className="mb-3 border-y border-neutral-300 py-3 shrink-0" aria-labelledby="starlink-map-status">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h5 id="starlink-map-status" className="text-base">Team connectivity</h5>
              <span className="text-xs text-neutral-600">Approximate area</span>
            </div>
            <ul className="grid gap-1 max-h-36 overflow-y-auto">
              {visibleStarlink.map((location) => (
                <li key={location.id}>
                  <button
                    type="button"
                    className={`w-full min-h-touch flex items-center gap-2 px-2 py-1 text-sm text-left rounded-DEFAULT
                      ${selectedStarlinkId === location.id ? 'bg-accent-100' : 'hover:bg-neutral-200'}`}
                    aria-pressed={selectedStarlinkId === location.id}
                    onClick={() => setSelectedStarlinkId(location.id)}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                      location.status === 'online' ? 'bg-presence-active'
                        : location.status === 'stale' ? 'bg-presence-idle' : 'bg-presence-offline'
                    }`} aria-hidden />
                    <span className="flex-1 min-w-0">
                      <span className="block font-semibold break-all">
                        {location.teamName ?? location.terminalId ?? 'Unassigned terminal'}
                      </span>
                      <span className="block text-neutral-700">
                        {location.status === 'unassigned' ? 'Unassigned, select to inspect'
                          : `${location.status} · ${locationAge(location.reportedAt)}`}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {selectedStarlink && (
              <StarlinkDetails
                location={selectedStarlink}
                onClose={() => setSelectedStarlinkId(null)}
              />
            )}
          </section>
        )}

        <ol className="overflow-y-auto flex-1 min-h-0">
          {visibleStops.map((s, index) => {
            const stopName = s.personNames.filter(Boolean).join(', ')
              || `${s.recordCount} scheduled ${s.recordCount === 1 ? 'visit' : 'visits'}`;
            return (
              <li
                key={s.id}
                className={`border-b border-neutral-300 ${dropTargetId === s.id ? 'bg-accent-100' : ''}`}
                onDragOver={(event) => {
                  if (!reorderEnabled) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  setDropTargetId(s.id);
                }}
                onDragLeave={() => setDropTargetId((current) => current === s.id ? null : current)}
                onDrop={(event) => dropStop(event, index)}
              >
                <div className="w-full py-2 flex items-start gap-2 min-h-[64px]">
                  <span className="w-7 h-7 rounded-full border border-neutral-400 shrink-0
                                   flex items-center justify-center text-sm tabular-nums">
                    {s.sequence}
                  </span>

                  {canReorder && selectedTeamId !== 'all' && (
                    <button
                      type="button"
                      draggable={reorderEnabled}
                      disabled={!reorderEnabled}
                      onDragStart={(event) => beginDrag(event, s.id)}
                      onDragEnd={() => {
                        setDraggedStopId(null);
                        setDropTargetId(null);
                      }}
                      className="inline-flex h-touch w-touch shrink-0 items-center justify-center rounded border border-neutral-300 bg-surface text-neutral-700 disabled:cursor-not-allowed disabled:opacity-40 enabled:cursor-grab enabled:active:cursor-grabbing"
                      aria-label={`Drag ${stopName} to a new position`}
                      title="Drag to reorder"
                    >
                      <Bars3Icon className="h-5 w-5" aria-hidden />
                    </button>
                  )}

                  <div className="flex-1 min-w-0">
                    <button onClick={() => { setSelected(s); onSelectStop(s.id); }} className="w-full min-w-0 text-left">
                      <span className="block font-heading font-semibold text-base break-words">
                        {stopName}
                      </span>
                      <span className="block text-base break-words">
                        {s.addressLine}
                      </span>
                      <span className="block text-sm text-neutral-700 truncate">
                        {s.cityLine} · {s.teamName}
                      </span>
                    </button>
                    {s.navigateUrl && (
                      <a href={s.navigateUrl} target="_blank" rel="noreferrer noopener"
                        className="inline-flex min-h-touch items-center text-sm font-semibold text-accent-700">
                        Navigate
                      </a>
                    )}
                  </div>

                  {canReorder && selectedTeamId !== 'all' && (
                    <div className="flex shrink-0 flex-col gap-1" aria-label={`Move ${stopName}`}>
                      <button
                        type="button"
                        className="inline-flex h-touch w-touch items-center justify-center rounded border border-neutral-300 bg-surface text-neutral-800 disabled:cursor-not-allowed disabled:opacity-30"
                        disabled={!reorderEnabled || index === 0}
                        onClick={() => moveStop(s.id, index - 1)}
                        aria-label={`Move ${stopName} up`}
                        title="Move up"
                      >
                        <ChevronUpIcon className="h-4 w-4" aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="inline-flex h-touch w-touch items-center justify-center rounded border border-neutral-300 bg-surface text-neutral-800 disabled:cursor-not-allowed disabled:opacity-30"
                        disabled={!reorderEnabled || index === visibleStops.length - 1}
                        onClick={() => moveStop(s.id, index + 1)}
                        aria-label={`Move ${stopName} down`}
                        title="Move down"
                      >
                        <ChevronDownIcon className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        {selected?.navigateUrl && (
          <a
            href={selected.navigateUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="btn btn-secondary btn-block mt-3"
          >
            Navigate to stop {selected.sequence}
          </a>
        )}
      </aside>
    </div>
  );
}

function locationAge(reportedAt: string | null): string {
  if (!reportedAt) return 'no report';
  const seconds = Math.max(0, Math.round((Date.now() - new Date(reportedAt).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return `${hours}h ago`;
}

function StarlinkDetails({
  location, onClose,
}: {
  location: StarlinkLocation;
  onClose: () => void;
}) {
  return (
    <article className="mt-3 border border-accent-300 bg-accent-100 rounded-lg p-3 max-h-[38vh] overflow-y-auto">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="card-kicker">Starlink telemetry</p>
          <h6 className="normal-case text-base break-all">
            {location.teamName ?? location.terminalId ?? 'Unassigned terminal'}
          </h6>
        </div>
        <button type="button" className="btn btn-ghost min-h-touch px-2" onClick={onClose}>
          Close
        </button>
      </div>

      <dl className="mt-2">
        {location.terminalId && (
          <TelemetryRow label="Starlink ID" value={location.terminalId} wrap />
        )}
        <TelemetryRow label="Last report" value={locationAge(location.reportedAt)} />
        <TelemetryRow label="Latitude" value={`${location.center[0].toFixed(5)} approximate`} />
        <TelemetryRow label="Longitude" value={`${location.center[1].toFixed(5)} approximate`} />
        <TelemetryRow label="Location precision" value="H3 area center, not exact GPS" />
        <TelemetryRow label="H3 cell" value={location.h3CellId} wrap />
        <TelemetryRow label="Velocity" value="Not provided by Starlink" />
        <TelemetryRow label="Direction of travel" value="Not provided by Starlink" />
        <TelemetryRow label="Signal" value={metric(location.signalPercent, '%')} />
        <TelemetryRow label="Latency" value={metric(location.latencyMs, ' ms')} />
        <TelemetryRow label="Packet loss" value={metric(location.packetLossPercent, '%')} />
        <TelemetryRow label="Download" value={metric(location.downlinkMbps, ' Mbps')} />
        <TelemetryRow label="Upload" value={metric(location.uplinkMbps, ' Mbps')} />
        <TelemetryRow label="Obstruction" value={metric(location.obstructionPercent, '%')} />
        <TelemetryRow label="Uptime" value={formatUptime(location.uptimeSeconds)} />
        <TelemetryRow
          label="Update reboot"
          value={location.secondsUntilSoftwareUpdateRebootPossible === null
            ? 'Not scheduled' : formatCountdown(location.secondsUntilSoftwareUpdateRebootPossible)}
        />
        <TelemetryRow label="Software" value={location.softwareVersion ?? 'Not reported'} wrap />
        <TelemetryRow label="Country" value={location.countryCode ?? 'Not reported'} />
        <TelemetryRow
          label="Territorial waters"
          value={location.inTerritorialWaters === null
            ? 'Not reported' : location.inTerritorialWaters ? 'Yes' : 'No'}
        />
        <TelemetryRow
          label="Active alerts"
          value={location.activeAlerts.length > 0
            ? location.activeAlerts.map(formatAlert).join(', ') : 'None'}
          wrap
        />
      </dl>
    </article>
  );
}

function TelemetryRow({
  label, value, wrap = false,
}: {
  label: string;
  value: string;
  wrap?: boolean;
}) {
  return (
    <div className="grid grid-cols-[104px_minmax(0,1fr)] gap-2 border-t border-accent-300 py-1.5 text-sm">
      <dt className="text-neutral-700">{label}</dt>
      <dd className={`m-0 font-semibold text-right ${wrap ? 'break-all' : ''}`}>{value}</dd>
    </div>
  );
}

function metric(value: number | null, suffix: string): string {
  return value === null ? 'Not reported' : `${Number(value.toFixed(2))}${suffix}`;
}

function formatUptime(seconds: number | null): string {
  if (seconds === null) return 'Not reported';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  return [days ? `${days}d` : '', hours ? `${hours}h` : '', `${minutes}m`]
    .filter(Boolean).join(' ');
}

function formatCountdown(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3_600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3_600)}h`;
}

function formatAlert(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
}

function starlinkIcon(unassigned: boolean): L.DivIcon {
  const stateClass = unassigned ? 'starlink-star-unassigned' : 'starlink-star-assigned';
  return L.divIcon({
    className: 'starlink-star-marker',
    html: `<span class="starlink-star ${stateClass}" aria-hidden="true">
      <svg viewBox="0 0 42 42" focusable="false">
        <path class="starlink-star-pulse" d="M21 2 26 14 39 15 29 23 32 37 21 30 10 37 13 23 3 15 16 14Z" />
        <path class="starlink-star-core" d="M21 5 25 16 36 17 27 24 30 34 21 28 12 34 15 24 6 17 17 16Z" />
      </svg>
    </span>`,
    iconSize: [42, 42],
    iconAnchor: [21, 21],
  });
}

/**
 * Numbered pin. The label is the sequence number only — §24 forbids names in
 * map labels, and a marker tooltip is a label.
 */
function pinIcon(stop: MapStop, teamColor: string): L.DivIcon {
  const isCurrent = stop.status === 'current';
  const isDone = stop.status === 'completed';
  const isSkipped = stop.status === 'skipped';

  const bg = isDone ? teamColor : isSkipped ? '#bab6b6' : '#ffffff';
  const fg = isDone ? '#ffffff' : '#201e1d';
  const ring = isCurrent ? '3px solid #d6006c' : `2px solid ${teamColor}`;

  return L.divIcon({
    className: 'veritas-pin',
    html:
      `<span style="display:flex;align-items:center;justify-content:center;` +
      `width:28px;height:28px;border-radius:999px;background:${bg};color:${fg};` +
      `border:${ring};font:600 13px 'Source Serif 4',serif;` +
      `box-shadow:0 1px 2px rgba(45,43,43,.14)">${stop.sequence}</span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}
